import { PayrollFieldViolation } from 'api/gql-utils/employeeCustomFields/types';
import { isEmptyFieldValue } from './utils';
import { AccessedFieldDefinition, CustomFieldValueMap, canonicalValueType } from './types';

const INTEGER_PATTERN = /^-?\d+$/;
const DECIMAL_PATTERN = /^-?\d*\.?\d+$/;

/**
 * Client-side validation for custom field values: required + type formats.
 * Instant feedback only — the HRIS-side validate call stays the authoritative
 * backstop (regex rules, cross-field rules, enum membership).
 */
const clientViolation = (field: string, description: string): PayrollFieldViolation => ({
  field,
  description,
  ruleId: null,
  ruleName: null,
  involvedFields: null,
  severity: 'ERROR',
});

export const validateCustomFieldValues = (
  definitions: AccessedFieldDefinition[],
  values: CustomFieldValueMap,
): PayrollFieldViolation[] => {
  const violations: PayrollFieldViolation[] = [];

  definitions
    .filter((def) => def.effectiveAccess === 'EDIT')
    .forEach((def) => {
      const key = def.fieldKey ?? '';
      const label = def.label ?? key;
      const raw = values[key];

      if (isEmptyFieldValue(raw)) {
        if (def.isRequired) {
          violations.push(clientViolation(key, `${label} is required`));
        }
        return;
      }

      switch (canonicalValueType(def.valueType)) {
        case 'INTEGER':
          if (!INTEGER_PATTERN.test(raw)) {
            violations.push(clientViolation(key, `${label} must be a whole number`));
          }
          break;
        case 'DECIMAL':
        case 'CURRENCY':
          if (!DECIMAL_PATTERN.test(raw)) {
            violations.push(clientViolation(key, `${label} must be a number`));
          }
          break;
        case 'DATE':
          if (Number.isNaN(new Date(raw).getTime())) {
            violations.push(clientViolation(key, `${label} must be a valid date`));
          }
          break;
        default:
          break;
      }
    });

  return violations;
};

/**
 * Restrict a value map to keys the caller can actually EDIT. Write payloads
 * must stay EDIT-only — HRIS rejects the entire write if any field lacks EDIT
 * access (e.g. a VIEW-only value that leaked into form state).
 */
export const filterToEditableValues = (
  definitions: AccessedFieldDefinition[],
  values: CustomFieldValueMap,
): CustomFieldValueMap => {
  const editableKeys = new Set(
    definitions.filter((def) => def.effectiveAccess === 'EDIT').map((def) => def.fieldKey ?? ''),
  );
  return Object.fromEntries(Object.entries(values).filter(([key]) => editableKeys.has(key)));
};

/**
 * HRIS stores MONEY values as JSON objects: { amount_paise, currency }.
 * The form edits rupees as a plain string; encode/decode at the API boundary.
 */
export const encodeFieldValuesForSubmit = (
  definitions: AccessedFieldDefinition[],
  values: CustomFieldValueMap,
): Record<string, unknown> => {
  const byKey = new Map(definitions.map((def) => [def.fieldKey ?? '', def]));
  return Object.fromEntries(
    Object.entries(values).map(([key, raw]) => {
      const def = byKey.get(key);
      if (def && canonicalValueType(def.valueType) === 'CURRENCY' && !isEmptyFieldValue(raw)) {
        return [key, { amount_paise: Math.round(parseFloat(raw) * 100), currency: 'INR' }];
      }
      return [key, raw];
    }),
  );
};

export const decodeFieldValues = (
  definitions: AccessedFieldDefinition[],
  fields: Record<string, unknown>,
): CustomFieldValueMap => {
  const byKey = new Map(definitions.map((def) => [def.fieldKey ?? '', def]));
  return Object.fromEntries(
    Object.entries(fields).map(([key, raw]) => {
      const def = byKey.get(key);
      if (
        def &&
        canonicalValueType(def.valueType) === 'CURRENCY' &&
        raw !== null &&
        typeof raw === 'object' &&
        'amount_paise' in (raw as Record<string, unknown>)
      ) {
        const paise = Number((raw as Record<string, unknown>).amount_paise);
        return [key, Number.isNaN(paise) ? null : String(paise / 100)];
      }
      return [key, raw === null || raw === undefined ? null : String(raw)];
    }),
  );
};

/**
 * Format a picked Date as YYYY-MM-DD using LOCAL date parts. toISOString()
 * converts to UTC first, which shifts IST midnight to the previous day.
 */
export const toLocalDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Input-level sanitization so numeric fields cannot receive text at all
 * (typing and paste). Keeps a single leading minus and, for decimals, one dot.
 */
export const sanitizeFieldInput = (def: AccessedFieldDefinition, raw: string): string => {
  switch (canonicalValueType(def.valueType)) {
    case 'INTEGER': {
      const cleaned = raw.replace(/[^\d-]/g, '');
      return (cleaned.startsWith('-') ? '-' : '') + cleaned.replace(/-/g, '');
    }
    case 'DECIMAL':
    case 'CURRENCY': {
      const cleaned = raw.replace(/[^\d.-]/g, '');
      const sign = cleaned.startsWith('-') ? '-' : '';
      const unsigned = cleaned.replace(/-/g, '');
      const [head, ...rest] = unsigned.split('.');
      return rest.length > 0 ? `${sign}${head}.${rest.join('')}` : `${sign}${head}`;
    }
    default:
      return raw;
  }
};
